<template>
  <v-card outlined data-testid="quote-grid" class="d-flex flex-column quote-card ">
    <v-row class="mb-2 pa-2 justify-center mt-1">
      <v-col cols="12" :sm="isProUser ? 4 : 5" class="d-flex justify-space-around flex-column align-center flex-sm-row-reverse">
        <div class="d-flex flex-column align-start">
          <span class="ml-3 light-input-text">
            {{ $t('quoteInfo.bestRate') }}
          </span>
          <span
            class="ml-3  line-height best-rate-and-total"
          >
            {{ bestRate && !isNaN(Number(bestRate.rfq_quote_px))
                ? bestRate.rfq_quote_px === 0 ? `${$t('broker.labels.waiting') }` :
                Number(bestRate.rfq_quote_px).toFixed(4)
                : "-"
            }}
          </span>
        </div>
      </v-col>

      <v-col
        cols="12"
        :sm="isProUser ? 4 : 2"
        class="d-flex flex-1 justify-center align-center flex-gap-2"
      >
        <v-btn
          elevation="0"
          tile
          color="primary"
          data-testid="quote-accept-best"
          @click="openConfirmation(bestRate)"
          :disabled="isAcceptDisabled"
          class="rounded"
        >
          <span class="white--text" :class="{ 'text--disabled': isAcceptDisabled }">
            {{ $t('instructions.accept') }}
          </span>
        </v-btn>

        <v-btn
          elevation="0"
          tile
          outlined
          class="rounded"
          @click="declineQuote"
          color="grey darken-2"
          :disabled="disableGrid"
        >
          <span class="white--text rounded" :class="{ 'text--disabled': disableGrid }">
            {{ $t('instructions.decline') }}
          </span>
        </v-btn>
      </v-col>
      <!-- Comment CFX Index for now -->
      <!-- <v-col cols="12" sm="5" class="d-flex flex-column flex-sm-row">
        <span class="mr-2">
          {{ $t('quoteInfo.cfxIndex') }}
        </span>
        <span class="gridSurfaceHighlight--text font-weight-bold font-size-rate line-height">
          -
        </span>
      </v-col> -->
      <v-col v-if="!isProUser" cols="12" sm="5" class="d-flex flex-2 justify-center flex-column align-center flex-sm-row">
        <div class="d-flex flex-column align-start">
          <span v-if="isBroker" class="mr-2 light-input-text">
            {{ $t('quoteInfo.markup') }}
          </span>
          <span v-else class="mr-2 light-input-text">
            {{ $t('quoteInfo.spread') }}
          </span>
          <span class="mr-2 line-height best-rate-and-total">
            {{ selectedSpread !== null ? selectedSpread : "-" }}
          </span>
        </div>
      </v-col>
      <v-col v-else cols="12" sm="4" />
    </v-row>

    <v-divider class="my-1"></v-divider>

    <v-row no-gutters class="my-2 d-flex justify-space-between flex-grow-1">
      <v-col :cols="isProUser ? 4 : 3">
        <div class="pt-1 pa-2 text-center light-input-text">
          {{ $t('quoteInfo.bank') }}
        </div>
      </v-col>
      <v-col cols="2">
        <div class="pt-1 pa-2 text-center light-input-text">
          {{ $t('quoteInfo.rate') }}
        </div>
      </v-col>
      <v-col v-if="!isProUser" cols="2" data-testid="quote-grid-spread-header">
        <div v-if="isBroker" class="pt-1 pa-2 text-center light-input-text">
          {{ $t('quoteInfo.markup') }}
        </div>
        <div v-else class="pt-1 pa-2 text-center light-input-text">
          {{ $t('quoteInfo.spread') }}
        </div>
      </v-col>
      <v-col v-if="!isProUser" cols="2" data-testid="quote-grid-ptax-header">
        <div class="pt-1 pa-2 text-center light-input-text">
          {{ $t('quoteInfo.spreadPtax') }}
        </div>
      </v-col>
      <v-col :cols="isProUser ? 3 : 2" data-testid="quote-grid-total-header">
        <div class="pt-1 pa-2 text-center light-input-text">
          {{ $t('quoteInfo.total') }}
        </div>
      </v-col>
      <v-col :cols="isProUser ? 3 : 1"/>
    </v-row>

    <v-row
      v-for="bank in bankList"
      :key="bank.id"
      data-testid="quote-row"
      :data-bank-id="bank.id"
      no-gutters
      class="align-center d-flex justify-space-between flex-grow-1 quote-row pr-3"
      :class="[bankRowStatus(bank).fontWeight, bankRowStatus(bank).backgroundColor]"
    >
      <v-col :cols="isProUser ? 4 : 3" align-self="stretch" style="background:#15171c">
        <div class="d-flex align-center justify-center text-center text-truncate px-3" style="height:100%">
          {{ bank.name }}
        </div>
      </v-col>

      <v-col cols="2">
        <div class="pa-3 text-center quote-rate" data-testid="quote-rate">
          {{
            bankQuotesPerRow(bank.id)?.rfq_quote_px === 0 ||  bankQuotesPerRow(bank.id) === undefined  ? `${$t('broker.labels.waiting') }` : bankQuotesPerRow(bank.id)?.rfq_quote_px?.toFixed(4)
          }}
        </div>
      </v-col>

      <v-col v-if="!isProUser" cols="2">
        <div class="pa-3 text-center">
          {{  bankQuotesPerRow(bank.id)?.rfq_quote_spread_avg || '-'}}
        </div>
      </v-col>

      <v-col v-if="!isProUser" cols="2">
        <div class="pa-3 text-center">
          {{  bankQuotesPerRow(bank.id)?.rfq_quote_spread_ptax || '-'}}
        </div>
      </v-col>

      <v-col :cols="isProUser ? 3 : 2">
        <div class="pa-3 font-weight-bold text-center">
          R$ {{ convertTotal(bank.id) ?? '' }}
        </div>
      </v-col>

      <v-col :cols="isProUser ? 3 : 1">
        <div class="pa-1 text-center">
          <v-btn
            elevation="0"
            tile
            outlined
            color="primary"
            small
            height="34"
            width="100%"
            class="rounded"
            @click="openConfirmation({
              ...bankQuotesPerRow(bank.id),
              rfq_id: bankQuotesPerRow(bank.id)?.rfq_id || quoteList?.[0]?.rfq_id
            })"
            :disabled="isRowAcceptDisabled(bank.id)"
            data-testid="quote-accept"
          >
            <span class="white--text" :class="{ 'text--disabled': isRowAcceptDisabled(bank.id) }">
              {{ $t('instructions.accept') }}
            </span>
          </v-btn>
        </div>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { mapState } from "pinia";
import {
  useSettingsStore,
} from "../store/index"
export default {
  name: "QuoteGrid",
  props: {
    amount: [String, Number],
    money: Object,
    loadingNewRfq: Boolean,
    gridActive: Boolean,
    disableQuoteButton: Boolean,
    disableGrid: Boolean,
    bankList: Array,
    bestRate: Object,
    selectedSpread: {
      type: [Number, String],
      default: null
    },
    isBroker: {
      type: Boolean,
      default: false
    },
    isPro: {
      type: Boolean,
      default: false
    },
    quote: Array,
    quoteList: Array,
  },
  mounted() {
    window.addEventListener('keydown', this.handleEscKey)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleEscKey)
  },
  computed: {
    ...mapState(useSettingsStore, [
      "isMockDataOn"
    ]),
    hasValidBestRate() {
      return this.isValidQuote(this.bestRate)
    },
    isAcceptDisabled() {
      if (this.disableGrid) return true
      const status = String(this.quoteList?.[0]?.rfq_status || '').toUpperCase()
      if (!status || status === 'CANCELLED' || status === 'REJECTED' || status === 'DEAL') {
        return true
      }
      return !this.hasValidBestRate
    },
    isProUser() {
      const storedRole = String(localStorage.getItem('userRole') || '').trim().toLowerCase()
      return this.isPro || storedRole === 'pro' || storedRole === 'gridpro'
    },
  },
  methods: {
    handleEscKey(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        if (!this.disableGrid && this.gridActive) {
          e.preventDefault()
          this.declineQuote()
        }
      }
    },
    isValidQuote(quote) {
      if (!quote) return false
      const px = Number(quote.rfq_quote_px)
      return !isNaN(px) && px > 0
    },
    isRowAcceptDisabled(bankId) {
      if (this.disableGrid) return true
      const status = String(this.quoteList?.[0]?.rfq_status || '').toUpperCase()
      if (status === 'CANCELLED' || status === 'REJECTED' || status === 'DEAL') {
        return true
      }
      const quote = this.bankQuotesPerRow(bankId)
      return !this.isValidQuote(quote)
    },
    openConfirmation(quoteItem) {
      if (!this.isValidQuote(quoteItem)) return
      this.$emit("openConfirmation", quoteItem)
    },
    declineQuote() {
      this.$emit("declineQuote")
    },
    bankQuotesPerRow(id)
    {
      return this.quoteList?.[0]?.rfq_quote?.find(q=>q.bank_id===id)
    },
    bankRowStatus(bank) {
      const bankQuote = this.bankQuotesPerRow(bank.id)
      const isBestRate = Boolean(
        this.gridActive && this.quote && bankQuote?.rfq_id && bankQuote?.rfq_quote_id === this.bestRate?.rfq_quote_id
      )
      const hasOtherQuote = Boolean(this.gridActive && bankQuote?.rfq_quote_id !== this.bestRate?.rfq_quote_id)
      return {
        fontWeight: isBestRate ? 'font-weight-bold' : 'font-weight-light',
        backgroundColor: this.disableGrid
          ? 'gridSurface'
          : isBestRate
            ? 'gridSurfaceHighlight'
            : hasOtherQuote
              ? 'gridAliveBlur'
              : 'gridSurface'
      }
    },
    convertTotal(id)
    {
      if(!this.amount || !this.quote) return
      const numberAmount =
        typeof this.amount === 'string'
          ? Number(this.amount.replace(/,/g, ""))
          : this.amount

      const total =
        (this.bankQuotesPerRow(id)?.rfq_quote_px || 0) * numberAmount

      return total.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    }
  }
}
</script>



<style scoped>
::v-deep div.v-radio.disabled.v-radio--is-disabled.theme--dark > div > i {
  color: #303030;
}
.quote-card {
  margin: 1.5rem auto;
  max-width: 1250px;
}
.light-input-text {
  font-weight: 300;
  font-size: 0.75rem;
  line-height: 1.25rem;
  letter-spacing: 0.03em;
  white-space: nowrap;
}
.best-rate-and-total {
  font-weight: bold;
  min-width: 74px;
  font-size: 20px !important;
  white-space: nowrap;
}
.quote-row {
  border-radius: 6px;
  margin-bottom: 6px;
  min-height: 3.4rem;
}
.quote-rate {
  font-size: 1.05rem;
  font-weight: 600;
}
.line-height {
  line-height: 26px;
}
.relative-container {
    position: relative;
}
.center-text {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
}

.v-input--radio-group__input {
  display: block
}
</style>
